import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { supabase } from '../config/supabase';
import { legacyParticipationEventTypeLabels, participationEventTypeOptions } from '../data/options';
import { lookupLabel } from '../data/lookups';
import type { LookupState } from '../types';

type MatchParticipationEventsProps = {
  matchId: string;
  lookups: LookupState;
};

type ParticipationEventRow = {
  id: string;
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_type: string;
  participation_event_reason: string | null;
  allocation_count_indicator: boolean;
};

type PositionRow = {
  id: string;
  position_code: string;
};

function eventTypeLabel(value: string): string {
  return participationEventTypeOptions.find((option) => option.value === value)?.label || legacyParticipationEventTypeLabels[value] || value;
}

export function MatchParticipationEvents({ matchId, lookups }: MatchParticipationEventsProps) {
  const [events, setEvents] = useState<ParticipationEventRow[]>([]);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const positionById = useMemo(() => new Map(positions.map((position) => [position.id, position])), [positions]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const eventsResult = await supabase
      .from('player_participation_events')
      .select('id, player_id, league_configuration_position_id, participation_event_type, participation_event_reason, allocation_count_indicator')
      .eq('match_event_id', matchId)
      .is('deleted_at', null)
      .order('occurred_at', { ascending: false });
    setLoading(false);

    if (eventsResult.error) {
      setError(eventsResult.error.message);
      return;
    }

    const rows = (eventsResult.data || []) as unknown as ParticipationEventRow[];
    setEvents(rows);
    const positionIds = [...new Set(rows.map((row) => row.league_configuration_position_id).filter(Boolean))] as string[];
    if (positionIds.length === 0) {
      setPositions([]);
      return;
    }
    const positionsResult = await supabase
      .from('league_configuration_positions')
      .select('id, position_code')
      .in('id', positionIds);
    if (positionsResult.error) {
      setError(positionsResult.error.message);
      return;
    }
    setPositions((positionsResult.data || []) as unknown as PositionRow[]);
  }, [matchId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Participation Log
        </Typography>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Line</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Event Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Counts Toward Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{lookupLabel(lookups, 'players', event.player_id)}</TableCell>
                  <TableCell>{positionById.get(event.league_configuration_position_id || '')?.position_code || '-'}</TableCell>
                  <TableCell>{eventTypeLabel(event.participation_event_type)}</TableCell>
                  <TableCell>{event.participation_event_reason || '-'}</TableCell>
                  <TableCell align="center">{event.allocation_count_indicator ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
              {events.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No participation events recorded for this match.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}
